import { ErrorService } from '@csp/web-common';
import {
  useMutation as useMutationInternal,
  MutationFunction,
  MutationKey,
  useQueryClient,
  InvalidateQueryFilters,
} from '@tanstack/react-query';
import { UseMutationState } from '../models/UseMutationState';

type MutationOptions<TData, TVariables> = {
  onSuccess?(data: TData): void;
  onError?(error: unknown, variables: TVariables): void;
  onMutate?(variables: TVariables): void;
  onSettled?: (data: TData | undefined, error: unknown | null, variables: TVariables) => void;
  mutationKey?: MutationKey;
  invalidateQueryFilters?: InvalidateQueryFilters;
};

export const useMutation = <TData = unknown, TVariables = unknown>(
  mutationFn: MutationFunction<TData, TVariables>,
  options?: MutationOptions<TData, TVariables>,
): UseMutationState<TData, TVariables> => {
  const queryClient = useQueryClient();

  const { error, data, isPending, mutate } = useMutationInternal({
    mutationFn,
    mutationKey: options?.mutationKey,
    onMutate: options?.onMutate,
    onSettled: options?.onSettled,
    onSuccess: async data => {
      if (options?.invalidateQueryFilters) {
        await queryClient.invalidateQueries(options.invalidateQueryFilters);
      }

      options?.onSuccess?.(data);
    },

    onError: (error, variables) => {
      ErrorService.handleError({ error });
      options?.onError?.(error, variables);
    },
  });

  return {
    data,
    isPending,
    error,
    mutate,
  };
};
